import React, { useState, useLayoutEffect, FunctionComponent } from "react";

//styles
import SV from "../../styles/variables";
import styled, { css } from "styled-components";
import "rc-slider/assets/index.css";
import "./slider.css";
//models
import { Option } from "../../models/select";

//mocks
import { colorOptions, brandOptions, lensOptions } from "./mockOptions";

//components
import FilterItem from "./filterItem";
import FilterColumn from "./filterColumn";
import Slider, { Range, createSliderWithTooltip } from "custom-rc-slider";
import { LightButton } from "../../components/buttons/button";

//hooks
import useOutsideClick from "../../hooks/useOutsideClick";

const Range2 = Range as any;

interface IProps {
  filterItems: (
    colors: string[],
    brands: string[],
    lenses: string[],
    min: number,
    max: number
  ) => void;
  clearFilters: () => void;
}

const Filter: FunctionComponent<IProps> = ({ filterItems, clearFilters }) => {
  const [colors, setStateColors] = useState(colorOptions());
  const [brands, setStateBrands] = useState(brandOptions());
  const [lenses, setStateLenses] = useState(lensOptions());
  const [openState, setStateOpen] = useState(false);
  const [prices, setStatePrices] = useState({ min: 0, max: 900 });
  const [updateSlider, setStateUpdateSlider] = useState(false);

  const ref = React.useRef(null);
  useOutsideClick(ref, e => {
    setStateOpen(false);
  });

  const ClickItem = (
    setState: React.Dispatch<React.SetStateAction<Option[]>>
  ) => (clickedOption: Option): void => {
    setState(options => {
      const newOptions = [...options];
      const currentOpt = newOptions.find(
        option => option.name === clickedOption.name
      );
      if (currentOpt) currentOpt.selected = !currentOpt.selected;
      return newOptions;
    });
  };

  const callFilterItems = () => {
    filterItems(
      colors.map(el => (el.selected ? el.name : "")).filter(el => el !== ""),
      brands.map(el => (el.selected ? el.name : "")).filter(el => el !== ""),
      lenses.map(el => (el.selected ? el.name : "")).filter(el => el !== ""),
      prices.min,
      prices.max
    );
  };

  const clearFiltersAndResetBrands = () => {
    setStateColors(colorOptions());
    setStateBrands(brandOptions());
    setStateLenses(lensOptions());
    setStatePrices({ min: 0, max: 900 });
    setStateUpdateSlider(true);
    filterItems([], [], [], 0, 900);
  };

  return (
    <FilterContainer ref={ref}>
      <FilterButton
        onClick={() => {
          setStateOpen(!openState);
        }}
      >
        <FilterTitle> Filter </FilterTitle>
        <FilterIcon src="./assets/images/filter2.JPG" alt="" />
      </FilterButton>
      {openState && (
        <FilterItemsContainer>
          <TopRow>
            <h4>Filter</h4>
            <ClearButton onClick={clearFiltersAndResetBrands}>
              clear
            </ClearButton>
          </TopRow>
          <Price> Price </Price>
          <SliderBox>
            <Range2
              min={0}
              max={900}
              range={true}
              defaultValue={[prices.min, prices.max]}
              onAfterChange={(newValues: any) => {
                setStatePrices({ min: newValues[0], max: newValues[1] });
              }}
              onChange={(newValues: any) => {
                setStateUpdateSlider(false);
              }}
              value={updateSlider ? [prices.min, prices.max] : undefined}
            />
          </SliderBox>
          <FilterColumns>
            <FilterColumn
              options={colors}
              width={124}
              updateSelected={ClickItem(setStateColors)}
              title="Color"
            />
            <FilterColumn
              options={brands}
              width={124}
              updateSelected={ClickItem(setStateBrands)}
              title="Brand"
            />
            <FilterColumn
              options={lenses}
              width={148}
              updateSelected={ClickItem(setStateLenses)}
              title="Type of Lens"
            />
          </FilterColumns>
          <ApplyFiltersButton onClick={callFilterItems}>
            Apply Filter
          </ApplyFiltersButton>
        </FilterItemsContainer>
      )}
    </FilterContainer>
  );
};

const filterButtonHeight = 32;
const filterButtonWidth = 68;
const dropDownGap = 4;
const dropDownWidth = 464;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  /* this means that the page will always have enough room to fit the filter component */
  ::after {
    content: "";
    top: 0;
    position: absolute;
    display: block;
    height: 700px;
    width: 1px;
    overflow-y: visible;
  }
`;

const FilterButton = styled.button`
  align-items: center;
  background-color: white;
  border-radius: 4px;
  border: solid 1px ${SV.colorP3};
  display: flex;
  height: ${filterButtonHeight}px;
  line-height: 1;
  justify-content: space-between;
  padding: 8px;
  width: ${filterButtonWidth}px;
`;
const FilterTitle = styled.span`
  color: ${SV.colorP2};
  font-size: 12px;
`;

const FilterIcon = styled.img`
  width: 16px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  h4 {
    font-size: 20px;
    font-weight: normal;
  }
  margin-bottom: 24px;
`;

const ClearButton = styled.button`
  width: 48px;
  height: 32px;
  border-radius: 4px;
  border: solid 1px #cedfff;
  background-color: #ffffff;
  font-size: 12px;
  color: #3a76eb;
  transition: all 0.3s ease-in-out;
  :hover {
    border-color: #3a76eb;
    background-color: #cedfff;
  }
`;

const Price = styled.h5`
  display: flex;
  font-size: 12px;
  font-weight: 500;
  color: #5d5d5d;
`;
const SliderBox = styled.div`
  display: flex;
  justify-content: center;
  height: 32px;
  width: 312px; /*354*/
  margin: 0 0 0 24px;
  padding: 20px 0 44px 0;
  /* margin-right: -6px; */
`;

const FilterItemsContainer = styled.div`
  align-items: space-around;
  background-color: white;
  border-radius: 4px;
  border: solid 1px #cedfff;
  display: flex column;
  justify-content: center;
  left: ${filterButtonWidth - dropDownWidth}px;
  line-height: 1;
  padding: 24px;
  position: absolute;
  text-align: left;
  top: ${filterButtonHeight + dropDownGap}px;
  width: ${dropDownWidth}px;
  z-index: 10;
  transform: translate(17.5%, -17.5%) scale(0.65);
  @media (min-width: ${dropDownWidth}px) {
    transform: translate(0, 0) scale(1);
  }
`;

const FilterColumns = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ApplyFiltersButton = styled(LightButton)`
  margin-top: 20px;
`;
export default Filter;
